import { CronExpressionParser } from 'cron-parser';
import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  Duration,
  format,
  formatDistanceToNow,
  parseISO,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { WeekDay } from '@/types';

export const DEFAULT_FORMAT_TYPE = 'MMM d, yyyy';

/**
 * Formats a UTC date string to show relative time for recent dates up to a month ago,
 * and a fixed date format for older dates.
 *
 * @param {string} utcTimestamp - The UTC date string to format.
 * @return {string} - The formatted date string.
 * @param {boolean} relativeFormatting - Whether to use relative formatting.
 */
export function formatDate(
  utcTimestamp: string,
  formatType = DEFAULT_FORMAT_TYPE,
  relativeFormatting = true
) {
  // Check if the input is a valid UTC timestamp
  if (
    !utcTimestamp?.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/)
  ) {
    // check for YYYY-MM-DD format and return a nice format
    if (utcTimestamp?.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return format(parseISO(utcTimestamp), formatType);
    } else {
      return utcTimestamp; // Return the input as is
    }
  }

  // Convert the UTC string to a Date object
  const date = parseISO(utcTimestamp);

  // Get the current datetime
  const currentDate = new Date();

  // If the date is within ±30 days of now, use relative formatting. We key
  // off of absolute distance — differenceInDays(now, future) is negative for
  // future timestamps, which breaks the upper-bound check otherwise.
  const daysDelta = differenceInDays(currentDate, date);
  if (relativeFormatting && Math.abs(daysDelta) < 30) {
    if (Math.abs(daysDelta) < 1) {
      const hoursDelta = differenceInHours(currentDate, date);

      if (hoursDelta === 0) {
        const minutesDelta = differenceInMinutes(currentDate, date);

        if (minutesDelta === 0) {
          return 'now';
        }
        const mag = Math.abs(minutesDelta);
        const unit = mag === 1 ? 'minute' : 'minutes';
        return minutesDelta > 0 ? `${mag} ${unit} ago` : `in ${mag} ${unit}`;
      }
      const mag = Math.abs(hoursDelta);
      const unit = mag === 1 ? 'hour' : 'hours';
      return hoursDelta > 0 ? `${mag} ${unit} ago` : `in ${mag} ${unit}`;
    }
    // date-fns' formatDistanceToNow with addSuffix renders both "X ago" and
    // "in X" correctly for past and future dates respectively.
    return formatDistanceToNow(date, { addSuffix: true });
  } else {
    // Otherwise, use a fixed date format
    return format(date, formatType);
  }
}

export function sToMs(seconds: number) {
  return seconds * 1000;
}

export function mToMs(minutes: number) {
  return sToMs(minutes * 60);
}

export function sToM(seconds: number) {
  return Math.floor(seconds / 60);
}

export function msToM(ms: number) {
  return Math.floor(ms / 60000);
}

function formatRelative(date: number) {
  const now = Date.now();
  // Convert both times to UTC before calculating difference
  const utcDate = new Date(date).getTime();
  const utcNow = new Date(now).getTime();
  const minuteDiff = differenceInMinutes(utcDate, utcNow);
  const hours = Math.floor(minuteDiff / 60);
  const minutes = minuteDiff % 60;

  return { hours, minutes };
}

function getCronTimeStamp(cron: string = '') {
  const interval = CronExpressionParser.parse(cron, {
    tz: 'UTC', // Use tz: 'UTC' instead of utc: true for v5.x compatibility
  });
  const ts = interval.next().getTime();

  return ts;
}

export function getCronRelativeTime(cron: string = '') {
  const ts = getCronTimeStamp(cron);
  return formatRelative(ts);
}

export function getCurrentTimeZone() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'longOffset',
  })
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName')?.value;
  return {
    tz,
    offset,
  };
}

export function padTime(time: number | undefined = 0) {
  return Number(time).toString().padStart(2, '0');
}

// time: HH:MM
// tz: timezone
export function convertToUTC(time: string, tz: string) {
  // Get current date to handle DST correctly
  const now = new Date();
  const [hours, minutes] = time.split(':').map(Number);

  // Create date object for today with the specified time
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes
  );

  const utcDate = fromZonedTime(localDate, tz);
  const utcDateObj = new Date(utcDate.toISOString());
  const utcTime = `${padTime(utcDateObj.getUTCHours())}:${padTime(utcDateObj.getUTCMinutes())}`;

  return {
    utcTime,
    diff: utcDateObj.getUTCDate() - now.getDate(),
  };
}

export function convertUTCToTimezone(utcTime: string, tz: string) {
  const now = new Date();
  const [hours, minutes] = utcTime.split(':').map(Number);

  // Create UTC date object
  const utcDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)
  );
  const localDate = toZonedTime(utcDate, tz);
  const localTime = `${padTime(localDate.getHours())}:${padTime(localDate.getMinutes())}`;

  return {
    localTime,
    diff: localDate.getDate() - now.getDate(),
  };
}

export const getRecentTimestamps = () => {
  const oneHour = 60 * 60 * 1000;
  const oneDay = 24 * oneHour;

  // Round to the nearest hour to prevent query key changes on every render.
  const nowRounded = Math.floor(Date.now() / oneHour) * oneHour;

  return {
    '24h': new Date(nowRounded - oneDay).toISOString(),
    '48h': new Date(nowRounded - 2 * oneDay).toISOString(),
    '5days': new Date(nowRounded - 5 * oneDay).toISOString(),
    '7days': new Date(nowRounded - 7 * oneDay).toISOString(),
    '20days': new Date(nowRounded - 20 * oneDay).toISOString(),
  };
};

export const convertDayToUTC = (day: WeekDay, timezoneDiffFromUTC: number) => {
  const dayInUtc = day + timezoneDiffFromUTC;
  if (dayInUtc > 6) {
    return 0;
  }
  if (dayInUtc < 0) {
    return 6;
  }
  return dayInUtc;
};

export const getFutureDateInISO = (duration: Duration) => {
  return add(new Date(), duration).toISOString();
};
